import { type Dispatch, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FileUp, Plus } from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "~/atoms/button";
import { FileListItem } from "~/molecules/FileListItem";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { UPLOAD_FILE_TYPES_ACCEPT } from "~/api/v2/constants";
import {
  validateFile,
  formatFileSize,
  type FileValidationError,
} from "~/utils/file-validation";
import { useDropZone } from "~/hooks/useDropZone";
import type { OnboardAction } from "~/pages/OnboardDocuments";

interface UploadStepProps {
  files: File[];
  dispatch: Dispatch<OnboardAction>;
  onNext: () => void;
}

function isFileInList(file: File, fileList: File[]): boolean {
  return fileList.some((f) => f.name === file.name && f.size === file.size);
}

export const UploadStep = ({ files, dispatch, onNext }: UploadStepProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (incoming: FileList | null) => {
    if (!incoming) return;

    const accepted: File[] = [];
    const errors: { name: string; reason: FileValidationError }[] = [];
    let duplicateCount = 0;

    for (const file of Array.from(incoming)) {
      if (isFileInList(file, [...files, ...accepted])) {
        duplicateCount++;
        continue;
      }

      const error = validateFile(file);
      if (error) {
        errors.push({ name: file.name, reason: error });
        continue;
      }

      accepted.push(file);
    }

    if (duplicateCount > 0) {
      toast.warning(t("library.onboardDocuments.steps.upload.duplicateFiles"));
    }

    for (const err of errors) {
      const message =
        err.reason === "unsupported_type"
          ? t("library.onboardDocuments.steps.upload.unsupportedType", {
              name: err.name,
            })
          : t("library.onboardDocuments.steps.upload.fileTooLarge", {
              name: err.name,
            });
      toast.error(message);
    }

    if (accepted.length > 0) {
      dispatch({ type: "SET_FILES", files: [...files, ...accepted] });
    }

    if (inputRef.current) inputRef.current.value = "";
  };

  const { isDragging, dropZoneProps } = useDropZone({ onDrop: handleFiles });

  const handleRemove = (file: File) => {
    dispatch({
      type: "SET_FILES",
      files: files.filter((f) => !isFileInList(file, [f])),
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {t("library.onboardDocuments.steps.upload.cardTitle")}
          </CardTitle>
          <CardDescription>
            {t("library.onboardDocuments.steps.upload.cardDescription")}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-3">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={UPLOAD_FILE_TYPES_ACCEPT}
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />

          {files.length === 0 ? (
            <div
              className={cn(
                "border-border flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-8 text-center",
                isDragging ? "bg-muted/50" : "hover:bg-muted/50",
              )}
              onClick={() => inputRef.current?.click()}
              {...dropZoneProps}
            >
              <FileUp className="text-muted-foreground size-8" />
              <p className="text-md">
                {t("library.onboardDocuments.steps.upload.clickOrDrag")}
              </p>
            </div>
          ) : (
            <div
              className={cn(
                "flex gap-3 rounded-lg border-2 border-dashed p-3",
                isDragging ? "border-border bg-muted/50" : "border-transparent",
              )}
              {...dropZoneProps}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => inputRef.current?.click()}
              >
                <Plus className="size-4" />
                {t("library.onboardDocuments.steps.upload.addMore")}
              </Button>
              <Button size="sm" onClick={onNext}>
                {t("library.onboardDocuments.steps.upload.continue", {
                  count: files.length,
                })}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {files.length > 0 && (
        <ul className="divide-border divide-y">
          {files
            .toSorted((a, b) => a.name.localeCompare(b.name))
            .map((file) => (
              <li key={`${file.name}-${file.size}`} className="bg-white p-2">
                <FileListItem
                  name={file.name}
                  subtitle={formatFileSize(file.size)}
                  onRemove={() => handleRemove(file)}
                />
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};
